import uomService from 'Services/api/uom.service'
import { IHeader } from 'components/DataTable/IHeader'
import useFetchApi, { isLoading } from 'hooks/useFetchApi/useFetchApi'
import useUOM from 'hooks/useUOM/useUOM'
import { groupBy, isEmpty, keys, sortBy } from 'lodash-es'
import { UOMContract } from 'models'
import { UOMClassContract } from 'models/UOMClassContract'
import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'
import nameof from 'utilities/nameof'
import toast from 'utilities/toast/toast'

interface IUOMsContext {
    uomsTableHeaders: IHeader[]
    uomClasses: UOMClassContract[]
    uoms: UOMContract[]
    systemUOMs: UOMContract[]
    customUOMs: UOMContract[]
    loading: boolean
    processing: boolean
    onRemoveUOMs: (uomIDs: number[]) => Promise<void>
    onUpdateDefaultUOM: (uomID: number) => Promise<void>
    onAddUOM: (newUOM: UOMContract) => Promise<void>
    onUpdateUOM: (id: number, updateUom: UOMContract) => Promise<void>
}

const fields = nameof<UOMContract>()

const UOMsContext = createContext({} as IUOMsContext)

function UOMsContextProvider({ children }: { children: ReactNode }) {
    const { t } = useTranslation()
    const { uoms, loading, setUOMs } = useUOM()

    const [
        uomClasses = [],
        getUOMClasses,
        getUOMClassesStatus,
        ,
        ,
        setUOMClasses,
    ] = useFetchApi(uomService.getUOMClasses)

    const [, removeUOMs, removeUOMsStatus] = useFetchApi(
        uomService.removeCustomUOMs,
        {
            showErrorToast: true,
            showSuccessToast: true,
            successMessage: t('uoms.message.success.remove_uoms'),
        },
    )

    const [, updateDefaultUOM, updateDefaultUOMStatus] = useFetchApi(
        uomService.updateDefaultUOM,
        {
            showErrorToast: true,
            showSuccessToast: true,
            successMessage: t('uoms.message.success.set_default_uom'),
        },
    )

    const [, createCustomUOM, createCustomUOMStatus] = useFetchApi(
        uomService.createCustomUOM,
        {
            showErrorToast: true,
            showSuccessToast: true,
            successMessage: 'uoms.message.success.created',
        },
    )

    const [, updateCustomUOM, updateCustomUOMStatus] = useFetchApi(
        uomService.updateCustomUOM,
        {
            showErrorToast: true,
            showSuccessToast: true,
            successMessage: 'uoms.message.success.updated',
        },
    )

    const systemUOMs = useMemo(
        () => uoms.filter((u) => !u.isCustomData) ?? [],
        [uoms],
    )

    const customUOMs = useMemo(() => uoms.filter((u) => u.isCustomData) ?? [], [
        uoms,
    ])

    const onUpdateDefaultUOM = useCallback(
        async (uomID: number) => {
            await updateDefaultUOM(uomID).then(() => {
                var updatedUOMs = uoms.map((uom) => {
                    if (uom.uomid === uomID) uom.isDefault = true
                    else if (uom.isDefault) uom.isDefault = false
                    return uom
                })

                setUOMs(updatedUOMs)
            })
        },
        [uoms, updateDefaultUOM, setUOMs],
    )

    const uomsTableHeaders: IHeader[] = useMemo(
        () => [
            {
                label: t('uoms.label.uom_name'),
                value: fields.name,
            },
            {
                label: t('uoms.label.symbol'),
                value: fields.symbol,
            },
            {
                label: t('uoms.label.description'),
                value: fields.description,
            },
            {
                label: t('uoms.label.default_uom'),
                value: fields.isDefault,
                switch: true,
                disabled: (uom: UOMContract) => uom.isDefault ?? false,
                helpText: t('uoms.message.tooltip.default_uom'),
            },
        ],
        [t],
    )

    const onUpdateUOMClasses = useCallback(
        (uomClassID: number, updatedUOMs: UOMContract[]) => {
            const newUOMClasses = uomClasses.map((uc) => {
                if (uc.uomClassID === uomClassID)
                    uc.uoms = updatedUOMs.filter(
                        (uom) => uom.uomClassID === uc.uomClassID,
                    )
                return uc
            })
            setUOMClasses(newUOMClasses)
        },
        [setUOMClasses, uomClasses],
    )

    const onRemoveUOMs = useCallback(
        async (uomIDs: number[]) => {
            var unremovedUOMIDs = (await removeUOMs(uomIDs)) ?? []
            var removedUOMIDs = uomIDs.filter(
                (uID) => !unremovedUOMIDs.includes(uID),
            )

            // Classes of removed UOMs
            var updatedUOMClassIDs = keys(
                groupBy(
                    uoms.filter((u) => removedUOMIDs.includes(u.uomid!)),
                    (i) => i.uomClassID,
                ),
            )

            var updateUOMs = uoms.filter(
                (u) => !removedUOMIDs.includes(u.uomid!),
            )

            if (!isEmpty(unremovedUOMIDs)) {
                var unremovableUOMNames: string[] = []
                updateUOMs.forEach((uom) => {
                    if (unremovedUOMIDs.includes(uom.uomid!)) {
                        uom.isConversionModifiable = false
                        unremovableUOMNames.push(uom.description ?? '1')
                    }
                })
                toast.error(
                    'uoms.message.error.unremovable_uoms' +
                        ' : ' +
                        unremovableUOMNames.join(', '),
                )
            }

            setUOMs(updateUOMs)

            // Update UOMs list of each uom class
            updatedUOMClassIDs.forEach((id) =>
                onUpdateUOMClasses(Number(id), updateUOMs),
            )
        },
        [removeUOMs, onUpdateUOMClasses, setUOMs, uoms],
    )

    const prepareUOMForm = useCallback((newUOM: UOMContract) => {
        if (!newUOM.referenceUOMID || newUOM.referenceUOMID === -1) {
            if (newUOM.enableConversion) newUOM.isBaseUOM = true
            newUOM.referenceUOMID = undefined
            newUOM.initialUnitForConversion = 1
            newUOM.conversion = 1
            newUOM.factor = 1
        } else {
            newUOM.isBaseUOM = false
            var initialUnit = newUOM.initialUnitForConversion!
            var refUOMUnit = newUOM.conversion!
            // example 1 kg (current) = 1000 g
            // -> conversion = 1000 / 1 = 1000 (n kg = n * 1000 g)
            newUOM.conversion = refUOMUnit / initialUnit
            // -> factor = 1 / 1000 = 0.001 (m g = m * 0.001 kg)
            newUOM.factor = initialUnit / refUOMUnit
        }
    }, [])

    const onAddUOM = useCallback(
        async (newUOM: UOMContract) => {
            prepareUOMForm(newUOM)
            await createCustomUOM(newUOM).then((res) => {
                if (res) {
                    // update uoms
                    const newUOMs = [...(uoms ?? []), res]
                    setUOMs(sortBy(newUOMs, (u) => u.name))
                    // update uom classes
                    onUpdateUOMClasses(res.uomClassID!, newUOMs)
                }
            })
        },
        [createCustomUOM, setUOMs, onUpdateUOMClasses, uoms, prepareUOMForm],
    )

    const onUpdateUOM = useCallback(
        async (id: number, updatedUOM: UOMContract) => {
            prepareUOMForm(updatedUOM)
            await updateCustomUOM(id, updatedUOM).then((res) => {
                if (res) {
                    // update uoms
                    const updatedUOMs = uoms.map((uom) => {
                        if (uom.uomid === id) return res
                        return uom
                    })
                    setUOMs(sortBy(updatedUOMs, (u) => u.name))
                    // update uom classes
                    onUpdateUOMClasses(res.uomClassID!, updatedUOMs)
                }
            })
        },
        [setUOMs, onUpdateUOMClasses, uoms, updateCustomUOM, prepareUOMForm],
    )

    useEffect(() => {
        getUOMClasses()
    }, [getUOMClasses])

    return (
        <UOMsContext.Provider
            value={{
                uomsTableHeaders,
                loading: loading || isLoading([getUOMClassesStatus]),
                uoms,
                uomClasses,
                systemUOMs,
                customUOMs,
                onRemoveUOMs,
                onUpdateDefaultUOM,
                processing: isLoading([
                    removeUOMsStatus,
                    updateDefaultUOMStatus,
                    createCustomUOMStatus,
                    updateCustomUOMStatus,
                ]),
                onAddUOM,
                onUpdateUOM,
            }}
        >
            {children}
        </UOMsContext.Provider>
    )
}

export default UOMsContextProvider

export const useUOMsContext = () => {
    const context = useContext(UOMsContext)
    return context
}
